import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { format } from 'date-fns';
import { VisitRecord, VisitFilters as VisitFiltersType } from '../types/VisitRecord';
import { visitApi } from '../services/api';
import VisitFilters from './VisitFilters';

const ActiveVisitsTab: React.FC = () => {
  const [visits, setVisits] = useState<VisitRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<VisitFiltersType>({
    startTime: null,
    endTime: null,
    organizationId: '',
    msisdn: '',
    visitToken: '',
  });
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<VisitRecord | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadVisits = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.startTime) params.startTime = filters.startTime.toISOString();
      if (filters.endTime) params.endTime = filters.endTime.toISOString();
      if (filters.organizationId) params.organizationId = filters.organizationId;
      if (filters.msisdn) params.msisdn = filters.msisdn;
      if (filters.visitToken) params.visitToken = filters.visitToken;

      const data = await visitApi.getActiveVisits(params);
      setVisits(data);
    } catch (error) {
      console.error('Failed to load visits:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load active visits',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  const handleCloseVisit = async () => {
    if (!selectedVisit) return;

    try {
      await visitApi.closeVisit(selectedVisit.visit_id);
      setSnackbar({
        open: true,
        message: 'Visit closed successfully',
        severity: 'success',
      });
      setCloseDialogOpen(false);
      setSelectedVisit(null);
      loadVisits();
    } catch (error) {
      console.error('Failed to close visit:', error);
      setSnackbar({
        open: true,
        message: 'Failed to close visit',
        severity: 'error',
      });
    }
  };

  const openCloseDialog = (visit: VisitRecord) => {
    setSelectedVisit(visit);
    setCloseDialogOpen(true);
  };

  const handleClearFilters = () => {
    setFilters({
      startTime: null,
      endTime: null,
      organizationId: '',
      msisdn: '',
      visitToken: '',
    });
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const columns: GridColDef[] = [
    {
      field: 'visit_token',
      headerName: 'Visit Token',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} size="small" color="primary" variant="outlined" />
      ),
    },
    {
      field: 'organization_id',
      headerName: 'Organization',
      width: 150,
    },
    {
      field: 'premises_id',
      headerName: 'Premises',
      width: 150,
    },
    {
      field: 'msisdn',
      headerName: 'MSISDN',
      width: 150,
      renderCell: (params: GridRenderCellParams) => params.value || 'N/A',
    },
    {
      field: 'entry_time',
      headerName: 'Entry Time',
      width: 180,
      renderCell: (params: GridRenderCellParams) =>
        format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss'),
    },
    {
      field: 'visit_duration_seconds',
      headerName: 'Duration',
      width: 120,
      renderCell: (params: GridRenderCellParams) => formatDuration(params.value),
    },
    {
      field: 'home_plmn_mcc',
      headerName: 'Home PLMN',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as VisitRecord;
        if (!row.home_plmn_mcc || !row.home_plmn_mnc) return 'N/A';
        return `${row.home_plmn_mcc}-${row.home_plmn_mnc}`;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 320,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as VisitRecord;
        if (row.exit_time) {
          return <Chip label="Closed" size="small" color="default" variant="outlined" />;
        }
        if (row.pending_exit_until) {
          const exitedAt = row.pending_exit_at
            ? format(new Date(row.pending_exit_at), 'yyyy-MM-dd HH:mm:ss')
            : 'n/a';
          const until = format(new Date(row.pending_exit_until), 'yyyy-MM-dd HH:mm:ss');
          return (
            <Chip
              label={`Pending exit · exited ${exitedAt} · until ${until}`}
              size="small"
              color="warning"
            />
          );
        }
        return <Chip label="Active" size="small" color="success" variant="outlined" />;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          color="secondary"
          size="small"
          onClick={() => openCloseDialog(params.row as VisitRecord)}
          title="Close Visit"
        >
          <ExitToAppIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Active Visits</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadVisits}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <VisitFilters
        filters={filters}
        onFiltersChange={setFilters}
        onSearch={loadVisits}
        onClear={handleClearFilters}
      />

      <DataGrid
        rows={visits}
        columns={columns}
        getRowId={(row) => row.visit_id}
        loading={loading}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        autoHeight
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />

      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)}>
        <DialogTitle>Close Visit</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to manually close this visit?
            <br />
            <br />
            <strong>Visit Token:</strong> {selectedVisit?.visit_token}
            <br />
            <strong>Organization:</strong> {selectedVisit?.organization_id}
            <br />
            <strong>Entry Time:</strong>{' '}
            {selectedVisit?.entry_time &&
              format(new Date(selectedVisit.entry_time), 'yyyy-MM-dd HH:mm:ss')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCloseVisit} color="secondary" variant="contained">
            Close Visit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ActiveVisitsTab;
